import React, { useState, useEffect } from 'react';
import { Users, UserCheck, UserX, TrendingUp, RefreshCw } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import '../styles/admin-dashboard.css';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0, activeUsers: 0, blockedUsers: 0, newToday: 0
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signupData, setSignupData] = useState([]);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      // Use secure RPC function instead of direct table query
      const { data: profiles, error } = await supabase
        .rpc('get_all_profiles');

      if (error) throw error;

      if (profiles) {
        const today = new Date().toDateString();
        const totalUsers = profiles.length;
        const blockedUsers = profiles.filter(p => p.role === 'blocked').length;
        const activeUsers = totalUsers - blockedUsers;
        const newToday = profiles.filter(p =>
          new Date(p.created_at).toDateString() === today
        ).length;

        setStats({ totalUsers, activeUsers, blockedUsers, newToday });
        setRecentUsers(profiles.slice(0, 5));

        // Build signup chart data (last 7 days)
        const last7Days = Array.from({ length: 7 }, (_, i) => {
          const d = new Date();
          d.setDate(d.getDate() - (6 - i));
          return {
            date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            signups: profiles.filter(p =>
              new Date(p.created_at).toDateString() === d.toDateString()
            ).length
          };
        });
        setSignupData(last7Days);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
    setLoading(false);
  };

  const statCards = [
    { label: 'Total Users', value: stats.totalUsers, icon: Users, color: '#4a9eff' },
    { label: 'Active Users', value: stats.activeUsers, icon: UserCheck, color: '#3ab86c' },
    { label: 'Blocked Users', value: stats.blockedUsers, icon: UserX, color: '#E8471E' },
    { label: 'New Today', value: stats.newToday, icon: TrendingUp, color: '#C8941A' },
  ];

  return (
    <div className="admin-dash-page">
      <div className="admin-dash-header">
        <div>
          <h1 className="admin-dash-title">Admin Dashboard</h1>
          <p className="admin-dash-subtitle">Overview of SynFin user activity</p>
        </div>
        <button className="admin-refresh-btn" onClick={fetchStats}>
          <RefreshCw size={15} className={loading ? 'spinning' : ''} />
          Refresh
        </button>
      </div>

      {/* Stat cards */}
      <div className="admin-stat-grid">
        {statCards.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="admin-stat-card">
            <div className="admin-stat-top">
              <span className="admin-stat-label">{label}</span>
              <div className="admin-stat-icon" style={{ background: `${color}18`, color }}>
                <Icon size={16} />
              </div>
            </div>
            <p className="admin-stat-value">
              {loading ? '—' : value.toLocaleString()}
            </p>
          </div>
        ))}
      </div>

      {/* Chart + Recent users */}
      <div className="admin-bottom-grid">
        {/* Signup chart */}
        <div className="admin-chart-card">
          <h3 className="admin-chart-title">New Signups — Last 7 Days</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={signupData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="signupGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#E8471E" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#E8471E" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e1e1e" />
              <XAxis dataKey="date" tick={{ fill: '#666', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#666', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: '#161616', border: '1px solid #2a2a2a', borderRadius: 8, color: '#f0ede8' }} />
              <Area type="monotone" dataKey="signups" name="New Users" stroke="#E8471E" fill="url(#signupGrad)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Recent users */}
        <div className="admin-chart-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 className="admin-chart-title">Recent Registrations</h3>
            <a href="/dashboard/users" className="admin-view-all">View all →</a>
          </div>
          {loading ? (
            <p style={{ color: '#666', fontSize: '0.875rem' }}>Loading...</p>
          ) : recentUsers.length === 0 ? (
            <p style={{ color: '#666', fontSize: '0.875rem' }}>No users yet.</p>
          ) : (
            <div className="admin-recent-list">
              {recentUsers.map(u => (
                <div key={u.id} className="admin-recent-row">
                  <div className="admin-recent-avatar">
                    {(u.full_name || u.email || 'U')[0].toUpperCase()}
                  </div>
                  <div className="admin-recent-info">
                    <p className="admin-recent-name">{u.full_name || 'No name'}</p>
                    <p className="admin-recent-email">{u.email}</p>
                  </div>
                  <span className={`admin-role-badge admin-role-${u.role || 'user'}`}>
                    {u.role || 'user'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
